import { useEffect, useState } from "react";
import { CheckCircle2, Clock, Copy, Gift, Loader2, Share2, Trophy, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { TopInviterBadge } from "@/components/TopInviterBadge";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { SocialShareRow, copyText } from "@/components/SocialShareRow";

const fill = (s, vars) => Object.entries(vars).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(v), s);

function Stat({ label, value, testId }) {
  return (
    <div className="bg-white rounded-xl border border-app-border px-4 py-3" data-testid={testId}>
      <div className="text-2xl font-heading font-semibold text-espresso leading-none">{value}</div>
      <div className="text-[11px] text-espresso/60 mt-1">{label}</div>
    </div>
  );
}

export const ReferralCard = ({ compact = false }) => {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/referrals/me").then(({ data: d }) => setData(d)).catch(() => setData(false));
  }, []);

  if (data === null) return <Loader2 className="w-5 h-5 animate-spin text-espresso/50" />;
  if (!data) return null;

  const link = `${window.location.origin}${data.link_path}`;
  const r = data.rules;
  const msg = `${t("ref.share_msg")} ${link}`;

  const copy = async () => ((await copyText(link)) ? toast.success(t("share.copied")) : toast.error(t("share.failed")));
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ text: msg, title: t("ref.title") }); } catch {/* cancelled */}
    } else {
      (await copyText(msg)) ? toast.success(t("share.copied")) : toast.error(t("share.failed"));
    }
  };

  return (
    <section className="space-y-4" data-testid="referral-card">
      <div className="bg-olive text-white rounded-2xl p-5 sm:p-6 space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-widest opacity-90">
          <Gift className="w-4 h-4" />
          {t("ref.title")}
          {data.top_inviter_months?.length > 0 && <TopInviterBadge months={data.top_inviter_months} />}
          {data.leading_this_month && (
            <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[10px] normal-case tracking-normal" data-testid="leading-this-month">
              <Trophy className="w-3 h-3" />{t("badge.leading")}
            </span>
          )}
        </div>
        <p className="text-sm leading-relaxed opacity-95" data-testid="referral-rules">
          {fill(t("ref.rules"), { n: r.batch_size, d1: r.signup_days, k: r.sub_threshold, d2: r.sub_days })}
          {" "}{t("ref.monthly_bonus")}
        </p>
        <div className="bg-white/15 rounded-xl px-3 py-2 flex flex-wrap items-center gap-2">
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            dir="ltr"
            className="text-sm font-mono break-all flex-1 min-w-[200px] underline decoration-white/50 hover:decoration-white"
            data-testid="referral-link"
          >
            {link}
          </a>
          <Button size="sm" variant="secondary" onClick={copy} className="gap-1.5" data-testid="referral-copy">
            <Copy className="w-4 h-4" />{t("share.copy_link")}
          </Button>
          <Button size="sm" variant="secondary" onClick={share} className="gap-1.5" data-testid="referral-share">
            <Share2 className="w-4 h-4" />{t("share.share")}
          </Button>
        </div>
        <SocialShareRow title={t("ref.title")} text={msg} link={link} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label={t("ref.stat_signups")} value={data.signups} testId="ref-stat-signups" />
        <Stat label={t("ref.stat_subscribed")} value={data.subscribed} testId="ref-stat-subscribed" />
        <Stat label={t("ref.stat_next")} value={data.next_signup_reward_in} testId="ref-stat-next" />
        <Stat label={t("ref.stat_days")} value={data.days_earned} testId="ref-stat-days" />
      </div>

      {!compact && (
        <div className="bg-white rounded-xl border border-app-border p-4 space-y-2" data-testid="referral-list">
          <div className="text-xs font-bold uppercase tracking-widest text-olive flex items-center gap-2">
            <Users className="w-4 h-4" />{t("ref.list_title")}
          </div>
          {data.referred.length === 0 ? (
            <p className="text-sm text-espresso/50" data-testid="referral-empty">{t("ref.empty")}</p>
          ) : (
            <ul className="divide-y divide-app-border">
              {data.referred.map((u, i) => (
                <li key={u.email} className="flex items-center gap-2 py-2 text-sm" data-testid={`referral-row-${i}`}>
                  <span className="flex-1 truncate text-espresso">{u.email}</span>
                  <span className="text-[11px] text-espresso/50">{new Date(u.joined_at).toLocaleDateString()}</span>
                  {u.subscribed ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full"><CheckCircle2 className="w-3 h-3" />{t("ref.subscribed")}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-espresso/60 bg-cream-2 px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" />{t("ref.joined")}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {data.rewards.length > 0 && (
            <ul className="pt-2 space-y-1" data-testid="referral-rewards">
              {data.rewards.map((rw, i) => (
                <li key={i} className="text-xs text-espresso/70 flex items-center gap-2">
                  <Gift className="w-3 h-3 text-olive" />
                  {fill(t(rw.kind === "signup" ? "ref.reward_signup" : "ref.reward_sub"), { d: rw.days, b: rw.batch })}
                  <span className="ml-auto rtl:ml-0 rtl:mr-auto text-espresso/40">{new Date(rw.granted_at).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};
